import { useEffect } from 'react'

const PIXEL_ID = '1328280338767913'

declare global {
  interface Window {
    fbq?: any
    _fbq?: any
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number
  }
}

let initialized = false

/**
 * Loads the official Meta (Facebook) Pixel base code and fires the initial
 * PageView. Injected at runtime since this project has no editable index.html.
 *
 * NOTE: This is intentionally DEFERRED until the browser is idle (after the
 * first paint) so the analytics script never competes for bandwidth / main
 * thread with the hero image and critical render. Tracking is not needed for
 * the initial paint, so deferring it makes the landing feel instant on entry.
 */
function initPixel() {
  if (
    initialized ||
    typeof window === 'undefined' ||
    typeof document === 'undefined'
  )
    return
  initialized = true

  // Official Meta Pixel base snippet (fbq stub + async script injection).
  if (!window.fbq) {
    const n: any = (window.fbq = function (...args: any[]) {
      n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args)
    })
    if (!window._fbq) window._fbq = n
    n.push = n
    n.loaded = true
    n.version = '2.0'
    n.queue = []

    const t = document.createElement('script')
    t.async = true
    t.src = 'https://connect.facebook.net/en_US/fbevents.js'
    const s = document.getElementsByTagName('script')[0]
    if (s && s.parentNode) {
      s.parentNode.insertBefore(t, s)
    } else {
      document.head.appendChild(t)
    }
  }

  window.fbq('init', PIXEL_ID)
  window.fbq('track', 'PageView')
}

/** Schedules pixel init for when the browser is idle (post first paint). */
function scheduleInit() {
  if (typeof window === 'undefined') return
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(initPixel, { timeout: 3000 })
  } else {
    setTimeout(initPixel, 1500)
  }
}

/**
 * Fires the standard InitiateCheckout event right before redirecting the user
 * to the external checkout. If the pixel has not loaded yet (init is
 * idle-deferred), it is initialized on the spot so the event is queued by the
 * fbq stub and sent as soon as the script is ready.
 */
export function trackInitiateCheckout() {
  if (typeof window === 'undefined') return
  initPixel()
  window.fbq?.('track', 'InitiateCheckout', {
    currency: 'USD',
    value: 19.99,
  })
}

/**
 * Ensures the Meta Pixel is initialized (deferred to idle) and fires PageView.
 * Safe to call once from <App />. Initialization is idempotent.
 */
export function useMetaPixel() {
  useEffect(() => {
    scheduleInit()
  }, [])
}
